import * as THREE from 'three';

// Shared singleton scene — one canvas, many bursts
let renderer = null;
let scene = null;
let camera = null;
let bursts = [];
let raf = null;
let canvas = null;
let dpr = 0;

function ensureScene() {
  if (renderer) return;
  canvas = document.createElement('canvas');
  canvas.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:9999;';
  document.body.appendChild(canvas);
  dpr = Math.min(window.devicePixelRatio || 1, 2);
  renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
  renderer.setPixelRatio(dpr);
  scene = new THREE.Scene();
  camera = new THREE.PerspectiveCamera(60, 1, 0.1, 100);
  camera.position.z = 10;

  function resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  resize();
  window.addEventListener('resize', resize);
}

function spawnBurst(x, y, color) {
  ensureScene();
  const count = 80;
  const positions = new Float32Array(count * 3);
  const velocities = new Float32Array(count * 3);

  const ndcX = (x / window.innerWidth) * 2 - 1;
  const ndcY = -((y / window.innerHeight) * 2 - 1);
  const worldPos = new THREE.Vector3(ndcX, ndcY, 0).unproject(camera);

  for (let i = 0; i < count; i++) {
    positions[i * 3] = worldPos.x;
    positions[i * 3 + 1] = worldPos.y;
    positions[i * 3 + 2] = 0;

    const angle = (i / count) * Math.PI * 2 + Math.random() * 0.3;
    const speed = 0.04 + Math.random() * 0.12;
    velocities[i * 3] = Math.cos(angle) * speed;
    velocities[i * 3 + 1] = Math.sin(angle) * speed;
    velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.06;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const mat = new THREE.PointsMaterial({
    color: new THREE.Color(color),
    size: 0.08,
    transparent: true,
    opacity: 1,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  });

  const points = new THREE.Points(geo, mat);
  scene.add(points);
  bursts.push({ points, velocities, geo, mat, life: 0, maxLife: 1.2 });
}

function tick() {
  if (bursts.length === 0) {
    raf = null;
    return;
  }
  raf = requestAnimationFrame(tick);

  const dt = 0.016;
  for (let i = bursts.length - 1; i >= 0; i--) {
    const b = bursts[i];
    b.life += dt;
    const t = b.life / b.maxLife;
    if (t >= 1) {
      scene.remove(b.points);
      b.geo.dispose();
      b.mat.dispose();
      bursts.splice(i, 1);
      continue;
    }
    const pos = b.geo.attributes.position.array;
    for (let j = 0; j < pos.length / 3; j++) {
      pos[j * 3] += b.velocities[j * 3] * dt * 60;
      pos[j * 3 + 1] += b.velocities[j * 3 + 1] * dt * 60;
      pos[j * 3 + 2] += b.velocities[j * 3 + 2] * dt * 60;
      // gravity
      b.velocities[j * 3 + 1] -= 0.003;
      // drag
      b.velocities[j * 3] *= 0.97;
      b.velocities[j * 3 + 1] *= 0.97;
    }
    b.geo.attributes.position.needsUpdate = true;
    b.mat.opacity = 1 - t;
    b.mat.size = 0.08 * (1 - t * 0.5);
  }
  renderer.render(scene, camera);
}

export function triggerBurst(x, y, color = '#0091ff') {
  spawnBurst(x, y, color);
  if (!raf) tick();
}

export function disposeBursts() {
  if (raf) cancelAnimationFrame(raf);
  raf = null;
  bursts.forEach(b => {
    scene?.remove(b.points);
    b.geo.dispose();
    b.mat.dispose();
  });
  bursts = [];
  if (renderer) {
    renderer.dispose();
    canvas?.remove();
    renderer = null;
    scene = null;
    camera = null;
    canvas = null;
  }
}
